import * as React from 'react';

import { useTranslations } from 'next-intl';

import { ThreadList, ThreadListEmpty, ThreadListNew } from '@/components/assistant-ui/thread-list';
import { useConfig, useConfigValue } from '@/components/providers/config-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { DynamicIcon } from '@/components/ui/dynamic-icon';
import { LinkLocationProvider } from '@/components/ui/link-location-context';
import { MarkdownLink } from '@/components/ui/markdown-link';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useCloseSidebarIfOverlay,
} from '@/components/ui/sidebar';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { useLocalizedConfigUrl } from '@/hooks/use-localized-config-url';
import { cn } from '@/lib/utils';

const DEFAULT_ICON_WIDTH = 120;

function SidebarIcon({ iconName }: { iconName: string }) {
  return (
    <div className="aui-sidebar-header-icon-wrapper flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
      <DynamicIcon name={iconName} className="aui-sidebar-header-icon size-4" />
    </div>
  );
}

export function ThreadListSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const t = useTranslations();
  const isReadOnly = useReadOnly();
  const closeSidebarIfOverlay = useCloseSidebarIfOverlay();
  const { assetBasePath, branding } = useConfig();
  const sidebarHeaderTitle = useLocalizedConfig<string>('navigation.sidebarTitle');
  const baseUrl = useConfigValue<string>('navigation.baseUrl');
  const sidebarIcon = useConfigValue<string>('navigation.sidebarIcon');
  const sidebarHeaderLink =
    useLocalizedConfigUrl({ path: 'navigation.sidebarLink', fallback: baseUrl }) || '/';

  const { icon, collapsedIcon, title } = branding?.components?.sidebar ?? {};
  const iconPath = icon?.path && assetBasePath ? `${assetBasePath}/${icon.path}` : null;
  const iconWidth = icon?.width ?? DEFAULT_ICON_WIDTH;
  const collapsedIconPath =
    collapsedIcon?.path && assetBasePath ? `${assetBasePath}/${collapsedIcon.path}` : null;
  const collapsedIconWidth = collapsedIcon?.width ?? 28;

  const headerContent = (
    <>
      {iconPath ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={collapsedIconPath ?? iconPath}
            alt=""
            width={collapsedIconPath ? collapsedIconWidth : iconWidth}
            height={collapsedIconPath ? collapsedIcon?.height : icon?.height}
            className={cn(
              'aui-sidebar-header-icon',
              collapsedIconPath
                ? 'hidden group-data-[collapsible=icon]:block'
                : 'group-data-[collapsible=icon]:block',
              !(collapsedIconPath ? collapsedIcon?.height : icon?.height) && 'h-auto',
            )}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          {collapsedIconPath && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={iconPath}
              alt=""
              width={iconWidth}
              height={icon?.height}
              className={cn(
                'aui-sidebar-header-icon group-data-[collapsible=icon]:hidden',
                !icon?.height && 'h-auto',
              )}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          )}
        </>
      ) : (
        <SidebarIcon iconName={sidebarIcon || 'MessagesSquare'} />
      )}
      {!title?.hidden && (
        <div className="aui-sidebar-header-heading me-6 flex flex-col gap-0.5 leading-none">
          <h1
            className="aui-sidebar-header-title text-sm font-semibold"
            style={{
              ...(title?.color ? { color: title.color } : undefined),
              ...(title?.fontSize ? { fontSize: title.fontSize } : undefined),
              ...(title?.fontWeight ? { fontWeight: title.fontWeight } : undefined),
            }}
          >
            {sidebarHeaderTitle || t('sidebar.header.title')}
          </h1>
        </div>
      )}
    </>
  );

  return (
    <LinkLocationProvider location="sidebar">
      <Sidebar {...props}>
        <SidebarHeader className="aui-sidebar-header mb-2 box-content">
          <div className="aui-sidebar-header-content flex items-center justify-between">
            <SidebarMenu>
              <SidebarMenuItem>
                {isReadOnly ? (
                  <SidebarMenuButton
                    size="lg"
                    className="cursor-default hover:bg-transparent active:bg-transparent"
                  >
                    {headerContent}
                  </SidebarMenuButton>
                ) : (
                  <SidebarMenuButton size="lg" asChild>
                    <MarkdownLink
                      href={sidebarHeaderLink}
                      className="font-normal text-inherit no-underline"
                      onClick={closeSidebarIfOverlay}
                    >
                      {headerContent}
                    </MarkdownLink>
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </div>
        </SidebarHeader>
        {!isReadOnly && (
          <div className="aui-sidebar-new-thread sticky top-0 z-10 bg-sidebar px-2 pb-1.5">
            <ThreadListNew />
          </div>
        )}
        <SidebarContent className="aui-sidebar-content px-2">
          <ThreadList />
          {isReadOnly && <ThreadListEmpty />}
        </SidebarContent>
        <SidebarRail />
      </Sidebar>
    </LinkLocationProvider>
  );
}
